import { Link } from 'react-router-dom';
import { ArrowRight, Check, Compass, Crosshair, Eye, Route, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { SectionDivider } from '@/components/decorative';
import { mission, positioning, strategy, vision } from '@/content/strategy';

const jump = [
  { id: 'vision', label: 'Vision', icon: Eye },
  { id: 'mission', label: 'Mission', icon: Compass },
  { id: 'positioning', label: 'Positioning', icon: Crosshair },
  { id: 'strategy', label: 'Strategy', icon: Route },
];

function SectionHead({ icon: Icon, eyebrow, title, lead }: { icon: typeof Eye; eyebrow: string; title: string; lead?: string }) {
  return (
    <div className="mb-8 md:mb-10 text-center">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-indigo-400" />
        <p className="text-sm text-indigo-300 uppercase tracking-wider font-medium">{eyebrow}</p>
      </div>
      <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">{title}</h2>
      {lead && <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">{lead}</p>}
    </div>
  );
}

export function VisionPage() {
  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-20 pb-10 md:py-20">
        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 rounded-full mb-6 border border-indigo-500/20">
            <Compass className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Vision</span>
          </div>
          <h1 className="text-hero-mobile md:text-hero-desktop font-extrabold tracking-heading mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">Where We&apos;re Going.</span>
            <br />
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">And How We Get There.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            What DialogMe is for, who it is for, and the order we build it in.
          </p>
        </div>

        {/* Jump links */}
        <nav className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12 md:mb-16">
          {jump.map(({ id, label, icon: Icon }) => (
            <a
              key={id}
              href={`#${id}`}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-800/40 border border-slate-700/40 text-sm text-slate-300 hover:text-white hover:border-indigo-400/30 transition-colors"
            >
              <Icon className="w-4 h-4 text-indigo-400" />
              {label}
            </a>
          ))}
        </nav>

        {/* Vision */}
        <section id="vision" className="scroll-mt-24 mb-12 md:mb-16">
          <SectionHead icon={Eye} eyebrow="Vision" title="The Conversation Is the Interface" />
          <Card variant="highlighted" className="p-6 sm:p-8 mb-6 text-center">
            <p className="text-xl sm:text-2xl font-bold text-white tracking-tight leading-snug mb-3">{vision.statement}</p>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">{vision.support}</p>
          </Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {vision.body.map((p) => (
              <Card key={p} variant="default" className="p-6">
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">{p}</p>
              </Card>
            ))}
          </div>
        </section>

        <SectionDivider variant="gradient" />

        {/* Mission */}
        <section id="mission" className="scroll-mt-24 my-12 md:my-16">
          <SectionHead icon={Compass} eyebrow="Mission" title="Finish the Job" />
          <Card variant="highlighted" className="p-6 sm:p-8 mb-6 text-center">
            <p className="text-xl sm:text-2xl font-bold text-white tracking-tight leading-snug mb-3">{mission.statement}</p>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">{mission.support}</p>
          </Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {mission.outcomes.map(([title, text]) => (
              <Card key={title} variant="default" className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-base font-semibold text-slate-100 tracking-tight">{title}</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
              </Card>
            ))}
          </div>
        </section>

        <SectionDivider variant="gradient" />

        {/* Positioning */}
        <section id="positioning" className="scroll-mt-24 my-12 md:my-16">
          <SectionHead icon={Crosshair} eyebrow="Positioning" title={positioning.line} lead={positioning.statement} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {positioning.versus.map(([title, text], i) => (
              <Card key={title} variant={i === 2 ? 'highlighted' : 'default'} className={i === 2 ? 'p-5' : 'p-5 opacity-80'}>
                <p className={i === 2 ? 'text-sm font-semibold text-indigo-300 mb-2' : 'text-sm font-semibold text-slate-400 mb-2'}>{title}</p>
                <p className="text-sm text-slate-300 leading-relaxed">{text}</p>
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <Card variant="elevated" className="p-6">
              <p className="text-sm text-emerald-300 uppercase tracking-wider font-medium mb-4">DialogMe is</p>
              <ul className="space-y-3">
                {positioning.is.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300 leading-relaxed">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
            <Card variant="default" className="p-6">
              <p className="text-sm text-slate-500 uppercase tracking-wider font-medium mb-4">DialogMe is not</p>
              <ul className="space-y-3">
                {positioning.isNot.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-400 leading-relaxed">
                    <X className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </section>

        <SectionDivider variant="gradient" />

        {/* Strategy */}
        <section id="strategy" className="scroll-mt-24 my-12 md:my-16">
          <SectionHead icon={Route} eyebrow="Strategy" title="One Step at a Time" lead={strategy.lead} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
            {strategy.choices.map((choice) => {
              const Icon = choice.icon;
              return (
                <Card key={choice.title} variant="default" className="p-6">
                  <Icon className="w-5 h-5 text-indigo-400 mb-3" />
                  <h3 className="text-base sm:text-lg font-semibold text-slate-100 tracking-tight mb-2">{choice.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{choice.description}</p>
                </Card>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {strategy.phases.map(([step, title, text]) => (
              <Card key={step} variant="elevated" className="p-5">
                <p className="text-xs font-mono text-indigo-400 mb-2">Phase {step}</p>
                <h3 className="text-base font-semibold text-white tracking-tight mb-2">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
              </Card>
            ))}
          </div>
          <Card variant="cta" className="p-6 sm:p-8">
            <p className="text-sm text-indigo-300 uppercase tracking-wider font-medium mb-2">Where it leads</p>
            <p className="text-slate-200 text-sm sm:text-base leading-relaxed">
              {strategy.longTerm} <span className="text-indigo-300 font-medium">{strategy.longTermPunch}</span>
            </p>
          </Card>
        </section>

        {/* CTA */}
        <Card variant="cta" className="text-center p-6 sm:p-8 md:p-10">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">See It Finish the Job</h2>
          <p className="text-slate-300 mb-6 leading-relaxed">No credit card. No setup. Just conversation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Try Live Demo
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                About DialogMe
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </main>
  );
}
